import { reactive } from '@vue/composition-api'
import { CompanyAPI, PipelineAPI } from '@/api/backend-client'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import { Pipeline } from '@/api/model/pipeline'
import { Company } from '@/api/model/company'


interface GeneratorOptions {
    includeInactive?: boolean
    includeOwn?: boolean
}

const state = reactive({
    connectionsLoaded: false,
    connectionsLoading: false,
    incomingConnections: [] as PipelineConnection[],
    outgoingConnections: [] as PipelineConnection[],
    ownPipelines: [] as Pipeline[],
})

// auto loading wont work in vue2
export function usePipelineConnections(autoLoad: boolean = true) {
    const defaultOptions: GeneratorOptions = {
        includeInactive: false,
        includeOwn: true,
    }

    async function loadPipelineConnections() {
        state.connectionsLoading = true
        state.connectionsLoaded = false
        state.incomingConnections.splice(0)
        state.incomingConnections.push(...await CompanyAPI.getIncomingConnections())
        state.outgoingConnections.splice(0)
        state.outgoingConnections.push(...await CompanyAPI.getOutgoingConnections())
        state.ownPipelines.splice(0)
        state.ownPipelines.push(...await PipelineAPI.getOwnPipelines())
        state.connectionsLoading = false
        state.connectionsLoaded = true
    }

    function buildIncomingConnections(options: GeneratorOptions = defaultOptions): PipelineConnection[] {
        if (!state.connectionsLoaded) {
            return []
        }

        const connections: PipelineConnection[] = []
        for (const connection of state.incomingConnections) {
            if (!options.includeInactive && !connection.isActive) {
                continue
            }

            connections.push(connection as PipelineConnection)
        }

        return connections
    }

    function buildOutgoingConnections(options: GeneratorOptions = defaultOptions): PipelineConnection[] {
        if (!state.connectionsLoaded) {
            return []
        }

        const connections: PipelineConnection[] = []
        for (const connection of state.outgoingConnections) {
            if (!options.includeInactive && !connection.isActive) {
                continue
            }

            connections.push(connection as PipelineConnection)
        }

        return connections
    }

    function buildIncomingPipelines(options: GeneratorOptions = defaultOptions): Pipeline[] {
        const pipelines: Pipeline[] = []

        if (options.includeOwn) {
            for (const pipeline of state.ownPipelines) {
                pipelines.push(pipeline as Pipeline)
            }
        }

        for (const connection of buildIncomingConnections(options)) {
            pipelines.push(connection.pipeline as Pipeline)
        }

        return pipelines
    }

    function buildOutgoingPipelines(options: GeneratorOptions = defaultOptions): Pipeline[] {
        const pipelines: Pipeline[] = []

        if (options.includeOwn) {
            for (const pipeline of state.ownPipelines) {
                pipelines.push(pipeline as Pipeline)
            }
        }

        for (const connection of buildOutgoingConnections(options)) {
            pipelines.push(connection.pipeline as Pipeline)
        }

        return pipelines
    }

    function buildIncomingCompanies(options: GeneratorOptions = defaultOptions): Company[] {
        const companies: Company[] = []

        if (options.includeOwn) {
            companies.push(CompanyAPI.currentCompany as Company)
        }

        for (const connection of buildIncomingConnections(options)) {
            companies.push(connection.sourceCompany as Company)
        }

        return companies
    }

    function buildOutgoingCompanies(options: GeneratorOptions = defaultOptions): Company[] {
        const companies: Company[] = []

        if (options.includeOwn) {
            companies.push(CompanyAPI.currentCompany as Company)
        }

        for (const connection of buildOutgoingConnections(options)) {
            companies.push(connection.targetCompany as Company)
        }

        return companies
    }

    if (state.connectionsLoaded === false && state.connectionsLoading === false && autoLoad) {
        loadPipelineConnections().then()
    }

    return {
        loadPipelineConnections,
        buildIncomingConnections,
        buildOutgoingConnections,
        buildIncomingPipelines,
        buildOutgoingPipelines,
        buildIncomingCompanies,
        buildOutgoingCompanies,
    }
}
